/**
 * Mirror the live recording status to the desktop menu bar (tray) icon.
 *
 * Pushes `(product, status)` to the Tauri shell whenever the status changes, and
 * resets the icon to `idle` when the recording UI unmounts. No-op off the desktop
 * shell — {@link setTrayStatus} short-circuits on the web/PWA build.
 */
import { useEffect, useRef } from "react"
import { setTrayStatus } from "../lib/desktop-tray"
import { useProduct } from "../lib/tenant"
import type { TTrayStatus } from "../lib/tray-status"

export function useDesktopTray(status: TTrayStatus): void {
  const product = useProduct()
  // Product is fixed for the session; mirror it into a ref so the unmount reset
  // doesn't need it in its dependency list (and never resets on a status change).
  const productRef = useRef(product)
  productRef.current = product

  useEffect(() => {
    void setTrayStatus(product, status)
  }, [product, status])

  useEffect(() => {
    return (): void => {
      void setTrayStatus(productRef.current, "idle")
    }
  }, [])
}
